/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable } from "@angular/core";
import { MergeChannelService } from "./merge-channel.service";
import { BusEvent, EventBusService, setWS1SessionStr, getWS1SessionStr, Logger } from "@html-core";

type SessionInfo = {
   horizonId: string;
   desktopSessions: any[];
};
/**
 * This service would try to merge tab when created, while close the old tab and transfer the session storage to the new tab for WS1 session reconnection.
 * This works with reconnect.service.ts
 * TabMergeService.initialize(horizonId) should be only called when url contains HorizonId on Web Client for on-perm env.
 * For V2 env, the behavior is not yet changed, since no blastCodec Supported yet.
 */
@Injectable()
export class TabMergeService {
   private timestamp;
   private appId;
   private horizonId;
   private pendingRequestResolve = null;
   private pendingRequestReject = null;
   private pendingRequestTimer = null;
   private logger: Logger = null;
   private handoverEverReceived = false;
   constructor(
      private eventBusService: EventBusService,
      private channel: MergeChannelService
   ) {
      this.logger = new Logger("TabMerge");
      this.timestamp = Date.now();
      this.appId = `app-id-${this.timestamp}-${Math.random().toString(36).substring(2, 9)}`;
   }

   async initialize(horizonId: string) {
      if (this.horizonId) {
         if (this.horizonId === horizonId) {
            this.logger.debug("TabMergeService already initialized for: " + horizonId);
         } else {
            this.logger.error(
               "TabMergeService already initialized for: " +
                  this.horizonId +
                  " but trying to reinitialize for: " +
                  horizonId
            );
            this.horizonId = horizonId;
         }
         return;
      }
      try {
         this.horizonId = horizonId;

         // Initialize the channel service
         await this.channel.initialize();

         // Set up message handlers
         this.setupMessageHandlers();

         // Broadcast this tab's creation
         this.broadcastTabCreation();

         this.eventBusService
            .listen(BusEvent.TabMergeResponse.MSG_TYPE)
            .subscribe((event: BusEvent.TabMergeResponse) => {
               // Handle the response from the reconnect service
               if (event.previousWS1SessionInfo) {
                  Logger.debug("TabMergeService: received response from reconnect service");
                  if (!this.pendingRequestResolve) {
                     Logger.error("TabMergeService: pendingRequestResolve is null");
                     return;
                  }
                  this.pendingRequestResolve(event.previousWS1SessionInfo);
               } else {
                  Logger.debug("TabMergeService: received rejection from reconnect service");
                  if (!this.pendingRequestReject) {
                     Logger.error("TabMergeService: pendingRequestReject is null");
                     return;
                  }
                  this.pendingRequestReject("rejected by strict check");
               }
               this.pendingRequestResolve = null;
               this.pendingRequestReject = null;
               clearTimeout(this.pendingRequestTimer);
               this.pendingRequestTimer = null;
            });

         this.logger.debug(`SharedItemsManager initialized with ID: ${this.appId} for horizonId: ${this.horizonId}`);
      } catch (error) {
         this.logger.error("Failed to initialize SharedItemsManager:" + error);
      }
   }

   setupMessageHandlers() {
      // Handle notifications from other tabs
      this.channel.onMessage("notification", (data, senderId) => {
         this.logger.debug(`Notification from ${senderId}:`);

         if (data.type === "tab-created") {
            this.handleNewTabCreated(data, senderId);
         }
      });

      // Handle handovers from other tabs
      this.channel.onMessage("handover", (data, senderId) => {
         this.logger.debug(`Handover from ${senderId}:`);

         if (data.type === "items-handover" && data.items) {
            this.handleItemsHandover(data, senderId);
         } else {
            this.logger.error("unexpected message content" + JSON.stringify(data));
         }
      });

      // Handle handover acknowledgments
      this.channel.onMessage("handover-ack", (data, senderId) => {
         if (data.type !== "items-received") {
            this.logger.error("unexpected message content" + JSON.stringify(data));
         }
         this.logger.debug(
            `Received handover ACK from ${senderId}: ${data.receivedCount} items received, close this tab now`
         );
         window.close();
      });
   }

   broadcastTabCreation() {
      this.channel.sendNotification({
         type: "tab-created",
         timestamp: this.timestamp,
         appId: this.appId
      });
   }

   async handleNewTabCreated(data, senderId) {
      // If the new tab has a newer timestamp, handover items
      if (data.timestamp > this.timestamp) {
         this.logger.debug(`New tab detected with newer timestamp: ${data.timestamp}`);

         // Request Sessions for hand over
         try {
            const items: SessionInfo | null = await this.getItemsFromSessionStorage(true);
            // Also handle over the session list(empty or not) to the new tab to have strict status sync for reconnect Application sessions
            if (items && items.horizonId === this.horizonId && items.desktopSessions) {
               this.handoverItemsToNewTab(items, senderId);
            } else {
               this.logger.debug("No items to handover, close this tab right after");
               window.close(); //TODO: test to see whether this is too aggressive
            }
         } catch (error) {
            this.logger.error("Find no items to hand over, close this tab:" + error);
            window.close();
         }
      }
   }

   async handoverItemsToNewTab(items, targetAppId) {
      this.logger.debug(`Handing over sessions for WS1 reconnection to ${targetAppId}`);
      // Send the handover
      await this.channel.sendHandover(targetAppId, {
         type: "items-handover",
         items: items,
         sourceAppId: this.appId
      });
      this.logger.debug("handover sent, waiting for ack");
   }

   async handleItemsHandover(data, senderId: string) {
      if (!data || !data.items) {
         this.logger.error("Invalid data received for handover");
         return;
      }
      let sessionInfo: SessionInfo = data.items;

      if (sessionInfo.horizonId !== this.horizonId) {
         this.logger.error(
            "Invalid horizonId received for handover:" + sessionInfo.horizonId + ", expected: " + this.horizonId
         );
         return;
      }
      if (sessionInfo.desktopSessions && Array.isArray(sessionInfo.desktopSessions)) {
         this.handoverEverReceived = true;
         this.logger.debug(`Received ${sessionInfo.desktopSessions.length} items from ${senderId}`);

         // merge items into session storage, the UX would listen and response
         this.saveItemsToSessionStorage(sessionInfo.desktopSessions);

         // Send acknowledgment
         await this.channel.sendHandoverAck(senderId, {
            type: "items-received",
            receivedCount: sessionInfo.desktopSessions.length,
            targetAppId: this.appId
         });
      }
   }

   /**
    * instead of directly accessing session storage, we should use the event bus to get from reconnect service to avoid dup codes
    * There should be no re-enter for this function, if not, request id should be added, and callback map used instead.
    * @returns
    */
   async getItemsFromSessionStorage(withExtraChecks: boolean): Promise<SessionInfo | null> {
      if (withExtraChecks) {
         return new Promise((resolve, reject) => {
            this.eventBusService.dispatch(new BusEvent.TabMergeRequest());
            this.pendingRequestResolve = resolve;
            this.pendingRequestReject = reject;
            this.pendingRequestTimer = setTimeout(() => {
               /**
                * When strict checking cannot be performed, we still need to hand over the unprocessed session list to the new tab.
                * This situation could occur when errors happen during the launching process.
                */
               if (this.handoverEverReceived) {
                  Logger.debug("Not able to do the strict check, return unprocessed session list");
                  this.getItemsFromSessionStorage(false)
                     .then((sessionInfo: SessionInfo | null) => {
                        resolve(sessionInfo);
                     })
                     .catch((error) => {
                        reject(error);
                     });
                  return;
               }
               reject("Request Timeout");
            }, 100);
         });
      } else {
         return new Promise((resolve, reject) => {
            const sessionInfoStr = getWS1SessionStr();
            if (sessionInfoStr) {
               try {
                  const sessionInfo: SessionInfo = JSON.parse(sessionInfoStr);
                  if (sessionInfo.horizonId === this.horizonId) {
                     resolve(sessionInfo);
                  } else {
                     reject(
                        "Invalid horizonId in session storage:" +
                           sessionInfo.horizonId +
                           ", expected: " +
                           this.horizonId
                     );
                  }
                  return;
               } catch (error) {
                  reject("Failed to parse session storage:" + error);
                  return;
               }
            }
            resolve(null);
         });
      }
   }

   async saveItemsToSessionStorage(syncedSessions: any[]) {
      try {
         // Merge with existing items in session storage, the merge would always be save
         const sessionInfo: SessionInfo | null = await this.getItemsFromSessionStorage(false);
         if (!sessionInfo) {
            setWS1SessionStr(
               JSON.stringify({
                  horizonId: this.horizonId,
                  desktopSessions: syncedSessions
               })
            );
            return;
         }
         let existingSessions = sessionInfo.desktopSessions || [];
         const existingSessionKeys = new Set(existingSessions.map((item) => item.key));
         const syncNewSessions = syncedSessions.filter((syncedSession) => !existingSessionKeys.has(syncedSession.key));

         const mergedSessions = [...existingSessions, ...syncNewSessions];
         // Use util function for now, Could write a storage service to handle this if it get complexer
         setWS1SessionStr(
            JSON.stringify({
               horizonId: this.horizonId,
               desktopSessions: mergedSessions
            })
         );
      } catch (error) {
         this.logger.error("Failed to save items to session storage:" + error);
      }
   }

   close() {
      if (this.channel) {
         this.channel.close();
      }
   }
}
